import React, { useCallback } from 'react';
import {
  Radio,
  RadioGroupMinimal,
} from '@lifeomic/chroma-react/components/Radio';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  unitPickerLabel: {
    id: 'unitPicker.label',
    defaultMessage: 'Unit picker',
  },
  unknownUnit: {
    id: 'unitPicker.unknownUnit',
    defaultMessage: 'Unknown',
  },
});

/** Sentinel for empty/whitespace-only units — Chroma Radio treats `value=""` as unchecked (falsy `checked || valueFromContext`). */
const EMPTY_UNIT_RADIO_VALUE = '__phc_empty_unit__';

function unitDisplayLabel(unit: string, unknownLabel: string): string {
  return unit.trim() ? unit : unknownLabel;
}

function toRadioValue(unit: string): string {
  return unit.trim() === '' ? EMPTY_UNIT_RADIO_VALUE : unit;
}

function fromRadioValue(radioValue: string): string {
  return radioValue === EMPTY_UNIT_RADIO_VALUE ? '' : radioValue;
}

type UnitPickerProps = {
  selectedUnit: string;
  units: string[];
  onSelect: (unit: string) => void;
};

export const UnitPicker = ({
  selectedUnit,
  units,
  onSelect,
}: UnitPickerProps) => {
  const { formatMessage } = useIntl();

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSelect(fromRadioValue(e.target.value));
    },
    [onSelect],
  );

  const radioValue = toRadioValue(selectedUnit);

  // RadioGroupMinimal keeps internal state from initial `value` only; remount when the
  // controlled selection or unit list changes so the radios stay in sync with props.
  return (
    <RadioGroupMinimal
      key={`${units.map(toRadioValue).join('|')}::${radioValue}`}
      value={radioValue}
      direction="row"
      onChange={onChange}
      aria-label={formatMessage(messages.unitPickerLabel)}
      fullWidth
    >
      {units.map((unit, index) => (
        <Radio
          key={`${unit}::${index}`}
          label={unitDisplayLabel(unit, formatMessage(messages.unknownUnit))}
          value={toRadioValue(unit)}
        />
      ))}
    </RadioGroupMinimal>
  );
};
